import styled, { keyframes } from 'styled-components';

export const PremadePage = styled.div`
	text-align: left;
`;

const rise = keyframes`
	to { transform: none; }
`;

/** List page heading with a side blurb. */
export const PhHead = styled.div`
	padding: 150px var(--pad) 42px;
	display: flex;
	align-items: flex-end;
	justify-content: space-between;
	gap: 26px;
	flex-wrap: wrap;

	h1 {
		font-size: clamp(46px, 7vw, 104px);
		line-height: 0.94;
	}

	/* homepage-hero row treatment: each line rises out of an overflow clip;
	   the padding/margin pair gives diacritics (Ć, Č) headroom above the
	   0.94 line box so the clip doesn't shave their accents off */
	h1 .row {
		display: block;
		overflow: hidden;
		padding-top: 0.14em;
		margin-top: -0.14em;
	}

	h1 .row span {
		display: inline-block;
		transform: translateY(110%);
		animation: ${rise} 1s cubic-bezier(0.2, 0.65, 0.2, 1) forwards;
	}

	h1 .row:nth-child(2) span {
		animation-delay: 0.12s;
	}

	h1 em {
		font-style: normal;
		color: var(--coral);
	}

	@media (prefers-reduced-motion: reduce) {
		h1 .row span {
			transform: none;
			animation: none;
		}
	}

	.side {
		flex: 0 0 360px;
		max-width: 360px;
	}

	@media (max-width: 1079px) {
		.side {
			flex: 1 1 auto;
		}
	}

	.side p {
		color: var(--brown);
		font-size: 17.5px;
		margin: 0;
	}

	.side .hand {
		font-family: var(--fh);
		font-weight: 600;
		color: var(--gold-ink);
		font-size: 26px;
		display: block;
		margin-top: 8px;
		transform: rotate(-1.5deg);
	}
`;

/**
 * /premade/author header: the byTag two-line h1 pattern, but the subject line
 * carries the author identity in the brand color — hand names keep their own
 * casing (lowercase Fjalla is on-brand, see the wordmark), collaborator logos
 * drop in pre-tinted. Description sits under the name, count + share share
 * one line on the right.
 */
export const AuthorHead = styled(PhHead)`
	align-items: stretch;

	h1 em {
		color: var(--teal-dark);
		text-transform: none;
	}

	h1 .hand-name {
		font-family: var(--fh);
		font-weight: 600;
	}

	/* clamp floors carry the mobile size, caps the desktop size */
	h1 img {
		display: inline-block;
		height: clamp(44px, 6.5vw, 100px);
		width: auto;
		vertical-align: baseline;
	}

	/* squarish logos (Nogač) read smaller at the same height — compensate */
	h1 img.sq {
		height: clamp(60px, 8.5vw, 132px);
	}

	/* wide logos whose artwork reads light (Vragolasti Tenis) */
	h1 img.lg {
		height: clamp(52px, 7.5vw, 118px);
	}

	h1 svg {
		vertical-align: text-bottom;
	}

	.desc {
		max-width: 620px;
		margin: 18px 0 0;
		color: var(--brown);
		font-size: 17.5px;
	}

	/* count pinned to the top corner, share stays at the bottom edge */
	.meta {
		display: flex;
		flex-direction: column;
		align-items: flex-end;
		justify-content: space-between;
		gap: 22px;
	}

	@media (max-width: 1079px) {
		.meta {
			flex: 1 1 100%;
			flex-direction: row;
			align-items: center;
		}
	}

	.meta .hand {
		font-family: var(--fh);
		font-weight: 600;
		color: var(--gold-ink);
		font-size: 26px;
		transform: rotate(-1.5deg);
		white-space: nowrap;
	}

	/* full share button on desktop, bare icon on narrow screens */
	.meta .share-icon {
		display: none;
		color: var(--brown);
	}

	@media (max-width: 1079px) {
		.meta .share-full {
			display: none;
		}

		.meta .share-icon {
			display: block;
		}
	}
`;

export const TagBar = styled.div`
	display: flex;
	gap: 24px;
	padding: 0 var(--pad) 40px;
	flex-wrap: wrap;
	align-items: baseline;

	a {
		font-family: var(--fd);
		font-size: 17px;
		letter-spacing: 0.4px;
		text-decoration: none;
		color: var(--mut);
		padding-bottom: 4px;
		border-bottom: 2px solid transparent;
		transition: 0.2s;
		cursor: pointer;
		text-transform: uppercase;
	}

	a:hover {
		color: var(--ink);
	}

	a.on {
		color: var(--teal-dark);
		border-color: var(--teal-dark);
	}

	/* the search entry is an action, not a filter: pushed to the end of the
	   rail and shaped like a small pill button */
	a.search-btn {
		margin-left: auto;
		align-self: center;
		display: inline-flex;
		align-items: center;
		gap: 8px;
		padding: 9px 18px 9px;
		border: 1.5px solid rgba(var(--ink-rgb), 0.3);
		border-radius: var(--r-pill);
		color: var(--ink);
		transition:
			border-color 0.2s,
			transform 0.18s;
	}

	a.search-btn:hover {
		border-color: var(--ink);
		transform: translateY(-1px);
	}

	a.search-btn svg path {
		fill: currentColor;
	}

	/* on a phone the rail wraps onto two rows, where full-size Fjalla tags
	   read as headings — the search pill shrinks with them */
	@media (max-width: 640px) {
		/* the rail wraps here, so the row gap is real vertical space — keep
		   the tags in their columns but pull the two lines together */
		row-gap: 10px;

		a {
			font-size: 14px;
		}

		/* the pill rejoins the row's baseline: centred in the flex line it
		   floated below the tag sharing its row. An inline-flex box takes its
		   baseline from its first item, so the icon has to sit on the baseline
		   too — otherwise the pill hangs 3px off the text beside it */
		a.search-btn {
			align-self: baseline;
			padding: 8px;
		}

		a.search-btn svg {
			align-self: baseline;
		}

		/* icon only down here; the label stays for screen readers */
		a.search-btn .lbl {
			position: absolute;
			width: 1px;
			height: 1px;
			overflow: hidden;
			clip-path: inset(50%);
			white-space: nowrap;
		}
	}
`;

export const CardsGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: clamp(16px, 2vw, 28px);
	padding: 26px var(--pad) 0;

	@media (max-width: 1079px) {
		grid-template-columns: 1fr 1fr;
	}

	@media (max-width: 640px) {
		grid-template-columns: 1fr;
	}

	.sale-sk {
		aspect-ratio: 4 / 5;
		border-radius: 18px;
	}
`;

/**
 * Premade quiz card as a sale ticket — the profile shelf's panorama card
 * (untouched 3:2 cover, title on paper, perforation with punched holes)
 * with a price coupon instead of the barcode: mono CIJENA label + Fjalla
 * price left, »u košaricu« right. Buying it is the moment the ticket gets
 * its barcode and reappears in the profile as the same card.
 */
export const SaleCard = styled.div`
	position: relative;
	display: block;
	border-radius: 18px;
	overflow: hidden;
	background: var(--panel-2);
	color: inherit;
	text-decoration: none;
	cursor: pointer;
	box-shadow: ${({ theme }) =>
		theme.mode === 'dark'
			? '0 16px 34px rgba(0, 0, 0, 0.45)'
			: '0 16px 34px rgba(var(--ink-rgb), 0.12)'};
	transition:
		transform 0.25s ease,
		box-shadow 0.25s ease;
	/* real punched holes on the horizontal perforation (56px coupon + half
	   of the 18px hole strip) — the page shows through the hole */
	--sh: 65px;
	-webkit-mask-image: radial-gradient(
			circle 10px at 0 calc(100% - var(--sh)),
			transparent 10px,
			#000 10.5px
		),
		radial-gradient(
			circle 10px at 100% calc(100% - var(--sh)),
			transparent 10px,
			#000 10.5px
		);
	-webkit-mask-composite: source-in;
	mask-image: radial-gradient(
			circle 10px at 0 calc(100% - var(--sh)),
			transparent 10px,
			#000 10.5px
		),
		radial-gradient(
			circle 10px at 100% calc(100% - var(--sh)),
			transparent 10px,
			#000 10.5px
		);
	mask-composite: intersect;

	&:hover {
		transform: translateY(-4px);
	}

	/* cover keeps its native 3:2 — never cropped; darkzone only under
	   the signature */
	.ph {
		position: relative;
		aspect-ratio: 3 / 2;
		overflow: hidden;
		background: #17130f;
	}

	.ph img.bg {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
		transition: transform 0.5s;
	}

	&:hover .ph img.bg {
		transform: scale(1.04);
	}

	/* title on paper in theme colors — no scrim fight with the cover */
	.tt {
		padding: 13px 17px 15px;
	}

	.tt h3 {
		font-size: 19px;
		line-height: 1.12;
	}

	.tt .im {
		display: block;
		margin-top: 6px;
		font-size: 10px;
		font-weight: 700;
		letter-spacing: 1.8px;
		text-transform: uppercase;
		color: var(--mut);
	}

	/* perforation: page-colored dots on the paper strip */
	.holes {
		height: 18px;
		background-color: var(--panel-2);
		background-image: radial-gradient(
			circle 4px at 8px 9px,
			var(--cream) 4px,
			transparent 4.5px
		);
		background-size: 16px 18px;
		background-repeat: repeat-x;
	}

	/* sale coupon: security lines, price where the owned ticket carries
	   its barcode, the author signing the ticket on the right */
	.stub {
		height: 56px;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		padding: 0 17px;
		background-image: repeating-linear-gradient(
			45deg,
			rgba(var(--ink-rgb), 0.025) 0 1px,
			transparent 1px 8px
		);
	}

	.stub .pr {
		display: flex;
		flex-direction: column;
		gap: 3px;
		min-width: 0;
	}

	.stub .pr .lb {
		font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
		font-size: 8.5px;
		letter-spacing: 2.2px;
		text-transform: uppercase;
		color: var(--mut);
		white-space: nowrap;
	}

	.stub .pr .eur {
		font-family: var(--fd);
		font-size: 21px;
		line-height: 1;
		color: var(--coral);
		white-space: nowrap;
	}

	/* the author signs the coupon in theme brand color (pre-tinted
	   brand/branddark logo variants, handwritten name, or the wordmark) */
	.stub .by {
		display: inline-flex;
		align-items: center;
		flex-shrink: 0;
		min-width: 0;
	}

	.stub .by .author-img {
		display: inline-flex;
		align-items: center;
	}

	.stub .by .author-img img {
		display: block;
		height: 20px;
		width: auto;
	}

	.stub .by .author-img.sq img {
		height: 24px;
	}

	.stub .by .author-img .v-neon {
		display: none;
	}

	.stub .by .author-tint {
		display: inline-flex;
		align-items: center;
	}

	.stub .by .author-hand {
		font-family: var(--fh);
		font-weight: 600;
		font-size: 21px;
		line-height: 1;
		text-transform: none;
		color: var(--teal-dark);
		white-space: nowrap;
	}
`;

export const MoreStrip = styled.div`
	padding: 70px var(--pad) 130px;
	text-align: center;

	.hand {
		font-family: var(--fh);
		font-weight: 600;
		color: var(--gold-ink);
		font-size: 30px;
		display: block;
		margin-bottom: 18px;
		transform: rotate(-1.2deg);
	}
`;

// ------------------- izlog: featured trio (1 big + 2 small) ------------------- //

/**
 * Featured cover card inside the izlog as a sale ticket; use with
 * className 'big' or 'small'. Both sizes share the grid ticket's anatomy —
 * photo stage with white title on the shade and the author signing the
 * photo, then a horizontal perforation and the paper price coupon at the
 * bottom — the big one is simply the grande print: taller stage, bigger
 * type.
 */
export const Fc = styled.a`
	position: relative;
	display: flex;
	flex-direction: column;
	border-radius: 18px;
	overflow: hidden;
	background: var(--panel-2);
	cursor: pointer;
	text-decoration: none;
	min-height: 0;
	box-shadow: ${({ theme }) =>
		theme.mode === 'dark'
			? '0 16px 34px rgba(0, 0, 0, 0.45)'
			: '0 16px 34px rgba(var(--ink-rgb), 0.12)'};
	transition:
		transform 0.25s ease,
		box-shadow 0.25s ease;
	/* real punched holes on the coupon perforation, same as the grid cards */
	--sh: 65px;
	-webkit-mask-image: radial-gradient(
			circle 10px at 0 calc(100% - var(--sh)),
			transparent 10px,
			#000 10.5px
		),
		radial-gradient(
			circle 10px at 100% calc(100% - var(--sh)),
			transparent 10px,
			#000 10.5px
		);
	-webkit-mask-composite: source-in;
	mask-image: radial-gradient(
			circle 10px at 0 calc(100% - var(--sh)),
			transparent 10px,
			#000 10.5px
		),
		radial-gradient(
			circle 10px at 100% calc(100% - var(--sh)),
			transparent 10px,
			#000 10.5px
		);
	mask-composite: intersect;

	&:hover {
		transform: translateY(-4px);
	}

	.ph {
		position: relative;
		flex: 1;
		min-height: 150px;
		overflow: hidden;
	}

	img.bg {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
		transition: transform 0.6s;
	}

	&:hover img.bg {
		transform: scale(1.03);
	}

	/* the photo alone is the stage: text shadows carry the title, the plate
	   adds only a quiet local gradient (no full-photo shade) */
	.plate {
		position: absolute;
		inset: auto 0 0 0;
		z-index: 2;
		padding: 26px 20px 14px;
		background: linear-gradient(
			180deg,
			transparent,
			rgba(24, 14, 2, 0.28) 55%,
			rgba(24, 14, 2, 0.5)
		);
	}

	h3 {
		font-size: clamp(19px, 1.7vw, 26px);
		line-height: 1.02;
		color: #fff;
		text-shadow:
			0 2px 14px rgba(24, 14, 2, 0.85),
			0 1px 3px rgba(24, 14, 2, 0.9);
	}

	/* meta in the grid ticket's mono voice, readable through its own shadow */
	.plate .meta {
		display: block;
		margin-top: 6px;
		font-size: 10px;
		font-weight: 700;
		letter-spacing: 1.8px;
		text-transform: uppercase;
		color: rgba(255, 255, 255, 0.92);
		text-shadow:
			0 2px 10px rgba(24, 14, 2, 0.85),
			0 1px 2px rgba(24, 14, 2, 0.9);
	}

	/* horizontal perforation: page-colored dots on the paper strip */
	.holes {
		height: 18px;
		background-color: var(--panel-2);
		background-image: radial-gradient(
			circle 4px at 8px 9px,
			var(--cream) 4px,
			transparent 4.5px
		);
		background-size: 16px 18px;
		background-repeat: repeat-x;
	}

	/* paper coupon: security lines, mono price label, Fjalla price in the
	   card price color, the author signing the ticket on the right */
	.stub {
		height: 56px;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 14px;
		padding: 0 18px;
		background-image: repeating-linear-gradient(
			45deg,
			rgba(var(--ink-rgb), 0.025) 0 1px,
			transparent 1px 8px
		);
	}

	.stub .pr {
		display: flex;
		flex-direction: column;
		gap: 3px;
		min-width: 0;
	}

	.stub .pr .lb {
		font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
		font-size: 8.5px;
		letter-spacing: 2.2px;
		text-transform: uppercase;
		color: var(--mut);
		white-space: nowrap;
	}

	.stub .pr .eur {
		font-family: var(--fd);
		font-size: 21px;
		line-height: 1;
		color: var(--coral);
		white-space: nowrap;
	}

	/* the author signs the coupon in theme brand color (pre-tinted
	   brand/branddark logo variants, handwritten name, or the wordmark) */
	.stub .by {
		display: inline-flex;
		align-items: center;
		flex-shrink: 0;
		min-width: 0;
	}

	.stub .by .author-img {
		display: inline-flex;
		align-items: center;
	}

	.stub .by .author-img img {
		display: block;
		height: 20px;
		width: auto;
	}

	.stub .by .author-img.sq img {
		height: 24px;
	}

	.stub .by .author-img .v-neon {
		display: none;
	}

	.stub .by .author-tint {
		display: inline-flex;
		align-items: center;
	}

	.stub .by .author-hand {
		font-family: var(--fh);
		font-weight: 600;
		font-size: 21px;
		line-height: 1;
		text-transform: none;
		color: var(--teal-dark);
		white-space: nowrap;
	}

	/* --------- grande print: same ticket, bigger stage and type --------- */

	&.big {
		grid-row: 1 / 3;
	}

	/* the big cover scroll-parallaxes like the homepage hero photo: taller
	   image, no transition so the rAF inline transform tracks scroll 1:1
	   (the inline transform replaces the hover zoom on this card) */
	&.big img.bg {
		height: 110%;
		transition: none;
		will-change: transform;
	}

	&.big .plate {
		/* extra top padding gives the plate gradient room to fade in */
		padding: clamp(36px, 4vw, 56px) clamp(20px, 3vw, 44px)
			clamp(20px, 2.6vw, 36px);
		/* the hero's text aligns with the page's --pad column */
		padding-left: calc(var(--pad) - var(--gut));
	}

	&.big h3 {
		font-size: clamp(28px, 3.2vw, 48px);
	}

	&.big .plate .meta {
		margin-top: 10px;
		font-size: 11px;
	}

	&.big .stub .pr .eur {
		font-size: 24px;
	}

	@media (max-width: 900px) {
		&.big .ph {
			min-height: 44vh;
		}

		&.small .ph {
			min-height: 30vh;
		}

		&.big .plate {
			padding-left: 20px;
		}
	}

	@media (max-width: 640px) {
		&.big h3 {
			font-size: 30px;
		}

		h3 {
			font-size: 24px;
		}

		.plate,
		&.big .plate {
			/* keep the fade-in room above the text on small screens too */
			padding: 26px 18px 18px;
		}
	}
`;

export const Izlog = styled.div`
	display: grid;
	grid-template-columns: 1.7fr 1fr;
	grid-template-rows: 1fr 1fr;
	gap: clamp(12px, 1.6vw, 22px);
	margin: 0 var(--gut);
	min-height: 62vh;

	@media (max-width: 900px) {
		grid-template-columns: 1fr;
		grid-template-rows: auto;
	}
`;

/**
 * Collaborators as an endless roll between the izlog and the category bands:
 * a full-bleed strip between two hairlines whose ends fade out, so the guest
 * logos enter and leave through the screen edge. The track is the homepage
 * Marquee driven by useMarqueeDrag — it auto-scrolls, pauses on hover and is
 * swipeable left/right.
 */
export const CollabStripBox = styled.div`
	/* the strip is a breather between the izlog and the category bands, so it
	   keeps clear air on both sides rather than hugging either — as padding,
	   not margin, so the Reveal wrapper can't collapse it away */
	padding: clamp(44px, 5vw, 78px) 0 clamp(28px, 3vw, 46px);

	/* the note sits on the page column above the rail; the rail itself runs
	   edge to edge, so it can't share the fading mask */
	.lead {
		display: inline-block;
		margin: 0 var(--pad) 8px;
		font-family: var(--fh);
		font-weight: 600;
		color: var(--gold-ink);
		font-size: clamp(21px, 2vw, 26px);
		transform: rotate(-1.3deg);
	}

	.rail {
		position: relative;
		display: block;
		overflow: hidden;
		padding: 15px 0;
		border-top: 1px solid rgba(var(--ink-rgb), 0.14);
		border-bottom: 1px solid rgba(var(--ink-rgb), 0.14);

		-webkit-mask-image: linear-gradient(
			90deg,
			transparent,
			#000 6%,
			#000 94%,
			transparent
		);
		mask-image: linear-gradient(
			90deg,
			transparent,
			#000 6%,
			#000 94%,
			transparent
		);
	}

	/* the tallest mark (a squarish logo) sets the strip's height; everything
	   else — dots included — rides its middle */
	.track {
		align-items: center;
	}

	/* no gap on the track: the drag hook wraps at half the scroll width, so
	   every item carries its own right margin instead (same reason the
	   homepage marquee spaces its slots with margins) */
	.track > * {
		flex: 0 0 auto;
		display: flex;
		align-items: center;
		margin-right: clamp(20px, 3vw, 46px);
	}

	.dot {
		width: 5px;
		height: 5px;
		border-radius: 50%;
		background: var(--teal-dark);
	}

	.mark {
		text-decoration: none;
		opacity: 0.8;
		transition: opacity 0.2s;
	}

	.mark:hover {
		opacity: 1;
	}

	.mark .author-img img {
		display: block;
		height: 26px;
		width: auto;
	}

	/* squarish logos (Nogač) read smaller at the same height — compensate,
	   but only part way: matching the wordmarks optically makes them tower */
	.mark .author-img.sq img {
		height: 34px;
	}

	.mark .author-img .v-neon {
		display: none;
	}

	/* authors without a logo sign in the brand color the tinted logos carry */
	.mark .author-hand {
		font-family: var(--fh);
		font-weight: 600;
		font-size: 24px;
		color: var(--teal-dark);
		white-space: nowrap;
	}

	@media (max-width: 640px) {
		.rail {
			padding: 12px 0;
		}

		.mark .author-img img {
			height: 22px;
		}

		.mark .author-img.sq img {
			height: 29px;
		}

		/* the rail speaks for itself on a phone — the note only ate a line */
		.lead {
			display: none;
		}
	}
`;

// --------------- chapters: full-bleed category bands --------------- //

export const Chapters = styled.div`
	padding: clamp(28px, 3.4vw, 52px) 0 130px;
	display: flex;
	flex-direction: column;
	gap: clamp(18px, 2.2vw, 30px);
`;

/**
 * One category band: the tinted background bleeds viewport-wide while the
 * heading and card grid stay on the --pad column; the rotated watermark
 * sticks to the screen edge. className drives the tint (c-film … c-rest)
 * and 'open' marks an expanded band (mobile shows 3 cards while collapsed).
 */
export const ChapterBand = styled.section`
	position: relative;
	overflow: hidden;
	padding: clamp(20px, 2.6vw, 38px) var(--pad);
	scroll-margin-top: 20px;

	&.c-novo {
		background: linear-gradient(
			120deg,
			rgba(219, 177, 84, 0.1),
			rgba(219, 177, 84, 0.03) 55%,
			transparent
		);
	}

	&.c-film {
		background: linear-gradient(
			120deg,
			rgba(241, 107, 70, 0.1),
			rgba(241, 107, 70, 0.03) 55%,
			transparent
		);
	}

	&.c-sport {
		background: linear-gradient(
			120deg,
			rgba(0, 128, 97, 0.1),
			rgba(0, 128, 97, 0.03) 55%,
			transparent
		);
	}

	&.c-put {
		background: linear-gradient(
			120deg,
			rgba(219, 177, 84, 0.14),
			rgba(219, 177, 84, 0.04) 55%,
			transparent
		);
	}

	&.c-muz {
		background: linear-gradient(
			120deg,
			rgba(241, 107, 70, 0.08),
			rgba(241, 107, 70, 0.02) 55%,
			transparent
		);
	}

	&.c-pov {
		background: linear-gradient(
			120deg,
			rgba(0, 128, 97, 0.08),
			rgba(0, 128, 97, 0.02) 55%,
			transparent
		);
	}

	&.c-rest {
		background: linear-gradient(
			120deg,
			rgba(var(--ink-rgb), 0.05),
			rgba(var(--ink-rgb), 0.015) 55%,
			transparent
		);
	}

	${({ theme }) =>
		theme.mode === 'dark'
			? `
	&.c-novo { background: linear-gradient(120deg, rgba(219,177,84,.08), rgba(219,177,84,.02) 55%, transparent); }
	&.c-film { background: linear-gradient(120deg, rgba(255,126,87,.09), rgba(255,126,87,.025) 55%, transparent); }
	&.c-sport { background: linear-gradient(120deg, rgba(0,230,175,.08), rgba(0,230,175,.02) 55%, transparent); }
	&.c-muz { background: linear-gradient(120deg, rgba(255,126,87,.06), rgba(255,126,87,.015) 55%, transparent); }
	&.c-pov { background: linear-gradient(120deg, rgba(0,230,175,.06), rgba(0,230,175,.015) 55%, transparent); }
	`
			: ''}

	.side {
		position: absolute;
		left: -4px;
		top: 14px;
		bottom: 0;
		display: flex;
		align-items: flex-start;
		pointer-events: none;
	}

	.side span {
		font-family: var(--fd);
		font-size: clamp(40px, 4.4vw, 68px);
		color: rgba(var(--ink-rgb), 0.07);
		text-transform: uppercase;
		writing-mode: vertical-rl;
		transform: rotate(180deg);
		line-height: 1;
		letter-spacing: 2px;
	}

	.chead {
		display: flex;
		align-items: baseline;
		gap: 16px;
		margin: 0 0 18px;
	}

	/* desktop leans on the edge watermark for the category name: the chead
	   keeps only the handwritten note; mobile (no watermark) shows the title */
	.chead h3 {
		font-size: clamp(26px, 3vw, 44px);
		display: none;
	}

	.chead .hand {
		font-family: var(--fh);
		font-weight: 600;
		color: var(--gold-ink);
		font-size: clamp(20px, 1.9vw, 26px);
		transform: rotate(-1.3deg);
	}

	.cgrid {
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		gap: clamp(12px, 1.5vw, 22px);
	}

	/* quiet expand pill under the row */
	.exp {
		display: flex;
		align-items: center;
		gap: 10px;
		margin: 18px 0 0;
		font-family: var(--fd);
		font-size: 13.5px;
		letter-spacing: 0.8px;
		text-transform: uppercase;
		color: var(--mut);
		padding: 9px 18px;
		border: 1.5px solid rgba(var(--ink-rgb), 0.22);
		border-radius: var(--r-pill);
		width: max-content;
		transition: 0.2s;
		background: none;
		cursor: pointer;
	}

	.exp:hover {
		color: var(--ink);
		border-color: var(--ink);
	}

	@media (max-width: 1079px) {
		.cgrid {
			grid-template-columns: 1fr 1fr;
		}
	}

	@media (max-width: 640px) {
		padding: 18px var(--pad);

		.cgrid {
			grid-template-columns: 1fr;
			gap: 24px;
		}

		.side {
			display: none;
		}

		.chead {
			flex-wrap: wrap;
			gap: 10px;
		}

		.chead h3 {
			display: block;
		}

		/* collapsed bands show 3 cards on mobile; the rest joins on expand
		   (the catch-all band always lists everything) */
		&:not(.open):not(.all) .cgrid > *:nth-child(n + 4) {
			display: none;
		}
	}
`;

// ---------------------------- quiz detail ---------------------------- //

export const QHead = styled.div`
	padding: 150px var(--pad) 34px;
	display: flex;
	align-items: flex-end;
	justify-content: space-between;
	gap: 24px;

	> div:first-child {
		flex: 1 1 auto;
		min-width: 0;
	}

	.tags {
		display: flex;
		gap: 16px;
		margin-bottom: 14px;
		flex-wrap: wrap;
	}

	.tags a {
		font-family: var(--fd);
		font-size: 13.5px;
		letter-spacing: 1.4px;
		color: var(--teal-dark);
		text-transform: uppercase;
		text-decoration: none;
	}

	.tags a:hover {
		color: var(--ink);
	}

	h1 {
		font-size: clamp(46px, 7.4vw, 110px);
		line-height: 0.94;
	}

	.hand {
		font-family: var(--fh);
		font-weight: 600;
		color: var(--gold-ink);
		font-size: clamp(24px, 2.2vw, 32px);
		transform: rotate(-1.6deg);
		flex: 0 0 360px;
		max-width: 360px;
		text-align: right;
	}

	@media (max-width: 1079px) {
		flex-wrap: wrap;

		.hand {
			flex: 1 1 100%;
			text-align: left;
		}
	}
`;

/* Mobile-only share icon row, sitting right above the cover photo. */
export const QShareRow = styled.div`
	display: none;

	@media (max-width: 1079px) {
		display: flex;
		justify-content: flex-end;
		padding: 0 var(--pad) 14px;
		color: var(--brown);
	}
`;

export const QMedia = styled.div`
	margin: 0 var(--gut);
	border-radius: var(--r);
	overflow: hidden;
	height: min(70vh, 680px);
	position: relative;

	img {
		width: 100%;
		height: 114%;
		object-fit: cover;
		will-change: transform;
	}

	.shade-top {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		height: 160px;
		z-index: 1;
		pointer-events: none;
		background: linear-gradient(rgba(24, 14, 2, 0.62), transparent);
	}
`;

export const QBody = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 34px;
	flex-wrap: wrap;
	padding: 44px var(--pad) 52px;

	p {
		color: var(--brown);
		font-size: 17.5px;
		max-width: 560px;
		margin: 0;
	}

	.acts {
		display: flex;
		gap: 16px;
		flex-wrap: wrap;
		align-items: center;
	}

	/* fixed width (natural size of the "podijeli poveznicu" label) so the
	   shorter "kopirano!" swap doesn't shift the row; !important because
	   ShareButton right-aligns via inline style */
	.acts .share-inline {
		width: 164px;
		justify-content: flex-start !important;
	}

	/* mobile drops the inline share row (icon lives above the photo instead);
	   !important because ShareButton sets display via inline style */
	@media (max-width: 1079px) {
		.acts .share-inline {
			display: none !important;
		}
	}
`;

export const QNote = styled.button`
	padding: 0 var(--pad) 140px;
	display: flex;
	align-items: center;
	gap: 12px;
	color: var(--mut);
	font-size: 15.5px;
	font-weight: 500;
	font-family: var(--fb);
	background: none;
	border: none;
	cursor: pointer;
	text-align: left;

	svg {
		color: var(--teal-dark);
		flex-shrink: 0;
	}

	&:hover {
		color: var(--ink);
	}
`;

/**
 * "Slični kvizovi" shelf at the foot of the premade single page: chapter-band
 * heading (handwritten note beside the title) over one row of sale tickets —
 * four across on desktop, a single column on mobile.
 */
export const QSimilar = styled.section`
	padding: 0 var(--pad) 140px;

	.shead {
		display: flex;
		align-items: baseline;
		gap: 16px;
		margin: 0 0 22px;
		flex-wrap: wrap;
	}

	.shead h2 {
		font-size: clamp(26px, 3vw, 44px);
	}

	.shead .hand {
		font-family: var(--fh);
		font-weight: 600;
		color: var(--gold-ink);
		font-size: clamp(20px, 1.9vw, 26px);
		transform: rotate(-1.3deg);
	}

	.sgrid {
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		gap: clamp(12px, 1.5vw, 22px);
	}

	@media (max-width: 1079px) {
		.sgrid {
			grid-template-columns: 1fr 1fr;
		}
	}

	@media (max-width: 640px) {
		.sgrid {
			grid-template-columns: 1fr;
		}
	}
`;

/** Corner quick-add circle shared by the grid cards and the featured izlog. */
export const QuickAddBtn = styled.button`
	position: absolute;
	top: 12px;
	right: 12px;
	z-index: 2;
	width: 42px;
	height: 42px;
	border-radius: 50%;
	border: none;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	color: var(--ink);
	background: ${({ theme }) =>
		theme.mode === 'dark' ? 'rgba(25,27,29,0.85)' : 'rgba(248,240,227,0.9)'};
	backdrop-filter: blur(8px);
	-webkit-backdrop-filter: blur(8px);
	transition: 0.2s;

	&:hover {
		background: var(--teal);
		color: var(--teal-deep);
	}

	&[aria-busy='true'] {
		pointer-events: none;
	}
`;

// Algolia SearchBox renders its own form; skin it to the design system.
export const SearchBoxSkin = styled.div`
	padding: 0 var(--pad) 26px;

	.ais-SearchBox-form {
		display: flex;
		gap: 10px;
		max-width: 640px;
	}

	.ais-SearchBox-input {
		flex: 1;
		font-family: var(--fb);
		font-size: 16.5px;
		color: var(--ink);
		background: var(--panel-2);
		border: 1.5px solid rgba(var(--ink-rgb), 0.3);
		border-radius: var(--r-pill);
		padding: 13px 22px;
		outline: none;
		transition: border-color 0.2s;
	}

	.ais-SearchBox-input:focus {
		border-color: var(--teal-dark);
	}

	.ais-SearchBox-submit,
	.ais-SearchBox-reset {
		font-family: var(--fd);
		font-size: 15px;
		letter-spacing: 0.4px;
		text-transform: uppercase;
		padding: 0 22px;
		border-radius: var(--r-pill);
		border: none;
		background: var(--teal);
		color: var(--teal-deep);
		cursor: pointer;
		transition: background 0.2s;
	}

	.ais-SearchBox-submit:hover {
		background: var(--teal-soft);
	}

	.ais-SearchBox-reset {
		background: transparent;
		border: 1.5px solid rgba(var(--ink-rgb), 0.3);
		color: var(--ink);
	}

	.ais-SearchBox-submit svg,
	.ais-SearchBox-reset svg {
		fill: currentColor;
		width: 14px;
		height: 14px;
	}
`;

/** Author mark sizing inside cards/detail (collaborator logos or hand name). */
export const AuthorMarkBox = styled.span<{ $onDark?: boolean }>`
	display: inline-flex;
	align-items: center;

	img {
		height: 26px;
		width: auto;
		display: block;
		${({ $onDark, theme }) =>
			$onDark || theme.mode === 'dark' ? '' : 'filter: none;'}
	}

	.hand-name {
		font-family: var(--fh);
		font-weight: 600;
		font-size: 24px;
		/* handwriting stays lowercase even inside uppercased meta rows */
		text-transform: none;
		color: ${({ $onDark }) => ($onDark ? 'var(--teal)' : 'var(--teal-dark)')};
	}

	a {
		text-decoration: none;
		display: inline-flex;
		align-items: center;
	}
`;
